// WHAT: React Context for managing global progress/sync state
// WHY:  Provides centralized progress tracking for multiple concurrent sync operations
// DOMAIN: Shared → Contexts
// DEPENDENCIES: React Context API
// RISKS:
//   - NOTE: Progress value is not updated by startSync/endSync - only by complete()
//   - NOTE: Multiple syncs tracked by count - no individual progress tracking
//   - NOTE: setTimeout in complete() may cause race conditions if called multiple times

import { useState, type ReactNode } from "react";

import { ProgressContext } from "./ProgressContext.shared";

/**
 * WHAT:
 *   Provider component that manages global progress state for sync operations,
 *   supporting multiple concurrent syncs with a counter-based approach.
 *
 * WHY:
 *   Provides a centralized way to track progress across multiple concurrent
 *   operations without managing individual progress states.
 *
 * BEHAVIOR:
 *   - Tracks active sync count (increments on startSync, decrements on endSync)
 *   - isAnySyncActive is true when count > 0
 *   - Progress is set to 100% on complete(), then resets after 300ms
 *   - Progress is reset to 0 on startSync
 *
 * RISKS:
 *   - NOTE: Progress value is not updated by startSync/endSync - only by complete()
 *   - NOTE: Multiple syncs tracked by count - no individual progress tracking
 *   - NOTE: setTimeout in complete() may cause race conditions if called multiple times
 *   - NOTE: endSync uses Math.max to prevent negative count - may hide bugs
 *
 * @param root0 - Props object
 *
 * @param root0.children - Child components
 *
 * @returns ProgressProvider component
 *
 */
export const ProgressProvider = ({ children }: { children: ReactNode }) => {
  // NOTE: Anzahl aktiver Syncs - wird bei startSync erhöht, bei endSync verringert
  const [activeCount, setActiveCount] = useState(0);
  // NOTE: Progress-Wert (0-100) - wird nur durch complete() gesetzt
  const [progress, setProgress] = useState(0);

  const startSync = () => {
    setActiveCount((count) => {
      const newCount = count + 1;
      return newCount;
    });
    setProgress(0);
  };

  const endSync = () => {
    setActiveCount((count) => {
      const newCount = Math.max(count - 1, 0);
      return newCount;
    });
  };

  const complete = () => {
    setProgress(100);
    setTimeout(() => {
      setProgress(0);
    }, 300);
  };

  const isAnySyncActive = activeCount > 0;

  return (
    <ProgressContext.Provider value={{ complete, endSync, isAnySyncActive, progress, startSync }}>
      {children}
    </ProgressContext.Provider>
  );
};
